import { sortBy } from '@Shared/Functions'
import Vue from 'vue'

let storeGlobal = null

export function updateStore(store) {
    storeGlobal = store
}

export const Settings = {
    namespaced: true,
    state: () => {
        return {
            /**
             * Integrações do canal de email.
             */
            integrationEmails: [],

            /**
             * Filtros que aparecem na lateral do helpdesk, carregado dinamicamente no primeiro acesso.
             */
            loadingCockpitHelpdeskFilters: false,
            cockpitHelpdeskFilters: null,

            /**
             * Os filtros que aparecem na lateral dos emails são dividos em dois: ao vivo e estático. Os filtros estáticos são cobertos
             * pelo `views_total` que atualiza quando o usuário aplica um filtro diferente.
             *
             * Já o ao vivo precisa de um caso especial porque não existe um registro de visualização associado, que é coberto por essa propriedade.
             * Para conseguir alimentar tanto a visualização dos meus atendimentos e não lidos, essa propriedade não pode ser um total, mas sim uma lista
             * dos ids dos atendimentos que passaram pelo filtro.
             *
             * Como nós temos uma cópia em memória dos registros não lidos, é possível filtrar essa lista para gerar o total tanto dos meus atendimentos (length completo)
             * como dos não lidos (filtrando baseado nos eventos não lidos).
             */
            cockpitHelpdeskLiveIdsInSearchMode: null,

            /**
             * Qualquer atributo que deva ser armazenado no cache.
             */
            cache: {},
            loadingCache: {},

            userPerfil: null,
            salaPerfil: null,
        }
    },
    getters: {
        cache: state => key => {
            return state.cache[key]
        },

        findIntegrationEmailById: state => id => {
            return state.integrationEmails.find(e => e.id == id)
        },

        hasUserPerfil: state => {
            return state.userPerfil != null
        },

        getSalaPerfilHtml: state => sala => {
            if (!state.salaPerfil) return ''
            if (!state.userPerfil) return ''

            let value = sala.atributos_extras[state.salaPerfil.nome]
            let option = state.userPerfil.atributos_extras.options.find(o => o.value == value)

            if (!option) return value

            return `
                <span style="background: ${option.color};" class="text-white px-1 rounded font-normal text-sm">${value}</span>
            `
        },
    },
    mutations: {
        putCache(state, { key, value }) {
            Vue.delete(state.loadingCache, key)
            Vue.set(state.cache, key, value)
        },

        deleteCache(state, { key }) {
            Vue.delete(state.cache, key)
        },

        putLoadingCache(state, { key }) {
            Vue.set(state.loadingCache, key, true)
        },

        setUserPerfil(state, userPerfil) {
            state.userPerfil = userPerfil
        },

        setSalaPerfil(state, salaPerfil) {
            state.salaPerfil = salaPerfil
        },

        /**
         * Atualiza os canais de integração de email.
         */
        updateIntegrationEmails(state, emails) {
            state.integrationEmails = emails
        },
    },
    actions: {
        async loadCockpitHelpdeskFilters({ state, dispatch, commit, rootState }) {
            state.loadingCockpitHelpdeskFilters = true

            let { data } = await axios.get('/core/api_interna/views', {
                params: {
                    all: true,
                    user_id: rootState.usuário.id,
                    modelo: 'chat_salas:helpdesk',
                },
            })

            state.loadingCockpitHelpdeskFilters = false
            state.cockpitHelpdeskFilters = data.map(view => {
                return { ...view, total: 0 }
            })

            dispatch('refreshCockpitHelpdeskFiltersTotals')
            commit('chat/updateHelpdeskFilter', state.cockpitHelpdeskFilters[0], { root: true })
            document.dispatchEvent(new CustomEvent('inbox:views-loaded'))
        },

        async refreshIntegrationEmails({ commit }) {
            let { data } = await axios.get('/integracao/emails?all', {
                params: {
                    status: 'on',
                    all: true,
                },
            })

            commit('updateIntegrationEmails', data)
        },

        async refreshCockpitHelpdeskFiltersTotals({ state }, opts = {}) {
            if (!opts.id) {
                state.cockpitHelpdeskFilters.forEach(filter => {
                    if (opts.except_id) {
                        if (filter.id != opts.except_id) {
                            filter.total = null
                        }
                    } else {
                        filter.total = null
                    }
                })
            }

            let filters = Object.keys(opts.filters || {})
                .filter(k => ['scope'].indexOf(k) == -1)
                .reduce((obj, key) => {
                    let value = opts.filters[key]
                    if (value) {
                        if (!Array.isArray(value) || (Array.isArray(value) && value.length > 0)) {
                            obj[key] = value
                        }
                    }
                    return obj
                }, {})

            let ids = []
            if (opts.id) {
                ids = [opts.id]
            } else {
                ids = state.cockpitHelpdeskFilters
                    .map(f => f.id)
                    .filter(id => {
                        if (opts.except_id) return id != opts.except_id
                        return true
                    })
            }

            let { data } = await axios.get('/core/api_interna/views_total', {
                params: {
                    views_ids: ids,
                    filters: filters,
                },
            })

            Object.keys(data).forEach(viewId => {
                let filter = state.cockpitHelpdeskFilters.find(filter => {
                    return filter.id == viewId
                })

                if (filter) filter.total = data[viewId]
            })
        },
    },
}

/**
 * Filtros que aparecem na lateral quando "Email" é selecionado nas abas.
 */
export function cockpitHelpdeskFilters() {
    if (storeGlobal.state.settings.cockpitHelpdeskFilters) return storeGlobal.state.settings.cockpitHelpdeskFilters
    if (storeGlobal.state.settings.loadingCockpitHelpdeskFilters) return { loading: true }
    storeGlobal.dispatch('settings/loadCockpitHelpdeskFilters')
    return { loading: true }
}

// Função global acessível pelo Chat/Store.js também.
window.cockpitHelpdeskFilters = cockpitHelpdeskFilters

export function getChatStatus(statusId) {
    if (statusId == 'null' || statusId == 'undefined' || !statusId) {
        return { name: __('Sem status'), color: '#71717A' }
    }

    return getAsyncCache('chat_status:' + statusId, '/chat/statuses/' + statusId, {
        name: __('Sem status'),
        color: '#71717A',
    })
}

export function getChatStatusesForSpace(spaceId) {
    return getAsyncCache(
        'chat_statuses:' + spaceId,
        '/chat/statuses?all&id_space=' + spaceId,
        { loading: true },
        statuses => {
            return sortBy(statuses, s => s.order)
        }
    )
}

export function getChatRoom(roomId) {
    return getAsyncCache('chat_salas:' + roomId, '/chat/api_interna/rooms/' + roomId, { name: __('Carregando...') })
}

export function getCoreSpaces() {
    return getAsyncCache('core_spaces', '/core/spaces?all', [])
}

export function getCoreSpace(spaceId) {
    return getAsyncCache('core_spaces:' + spaceId, '/core/spaces/' + spaceId, { nome: __('Carregando') })
}

export function getCoreDepartment(departmentId) {
    if (!departmentId) {
        return { nome: '--' }
    }

    return getAsyncCache('core_departments:' + departmentId, '/core/departamentos/' + departmentId, {
        nome: __('Carregando'),
    })
}

export function getAsyncCache(key, url, loading, callback = null) {
    let record = storeGlobal.state.settings.cache[key]
    if (record) return record
    if (storeGlobal.state.settings.loadingCache[key]) {
        return loading
    }

    ;(async () => {
        storeGlobal.commit('settings/putLoadingCache', { key })
        let { data } = await axios.get(url)
        if (callback) data = callback(data)
        storeGlobal.commit('settings/putCache', { key, value: data })
    })()

    return loading
}
